"use client";

import React, { useRef, useState, useEffect } from "react";

import Image from "next/image";
import CornerChip from "@/components/global/ui/CornerChip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import {
  HLSVideoPlayer,
  SanityVideoPlayer,
} from "@/components/ui/SanityVideoPlayer";
import { Dialog, DialogContent } from "@/shadcn/ui/dialog";

import { PageAboutSchema } from "@/sanity/pages/PageAboutTemplate";

import volumeMute from "@/public/icons/Volume--mute--filled.svg";
import volumeUnmute from "@/public/icons/Volume--up--filled.svg";
import maximize from "@/public/icons/Maximize.svg";
import screenEffect from "@/public/about/screen-effect.svg";
import { PageInnerCircleSchema } from "@/sanity/pages/PageInnerCircleTemplate";
import Chip from "../global/ui/Chip";
import Divider from "../global/ui/Divider";
import DividerGreenLine from "../global/ui/DividerGreenLine";
import ChipScrollingBanner from "../global/ui/ChipScrollingBanner";
import GridNoise from "../global/ui/GridNoise";
import Link from "next/link";

export default function LastSection({
  data,
  className,
}: {
  data: PageInnerCircleSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative min-h-[100rem] lg:min-h-[122.5rem] flex lg:pb-[10rem] pt-[10rem]",
        className,
      )}
    >
      <Image
        className="hidden md:block absolute left-0 top-0 !w-full !h-full object-cover"
        src={data?.lastImage}
        quality={100}
        alt="Background image"
      />
      <Image
        className="lg:hidden absolute left-0 top-0 !w-full !h-full object-cover"
        src={data?.lastImageMobile}
        quality={100}
        alt="Background image"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-transparent to-[#0A1A1D] w-full h-[30%]" />
      <GridNoise />

      <div className="relative flex flex-col items-center mx-auto px-[2rem] lg:px-0 lg:pt-[20rem]">
        <Chip
          className="prose-img:!w-[2rem]"
          image={data?.lastChip?.icon}
          text={data?.lastChip?.text}
        />
        {data?.lastTitle && (
          <div className="w-[min(130rem,100%)] richtext richtext-center prose-headings:gradient-title-white prose-headings:text-[7rem] lg:prose-headings:text-[14rem] prose-headings:font-ivy prose-headings:text-transparent prose-p:text-white/60 prose-img:my-0 prose-headings:!mb-0 prose-headings:px-[1rem] prose-headings:pb-[2.75rem]">
            <PortableText
              value={data.lastTitle}
              components={richTextComponent}
            />
          </div>
        )}
        <DividerGreenLine
          className="w-[40.75rem] lg:w-[94.75rem] my-[3rem]"
          greenLineClassName="lg:w-[30rem]"
        />

        {data?.lastCta && (
          <div className="w-[40rem]">
            <CTACornerButton data={data.lastCta} />
          </div>
        )}
        {data?.lastCta2 && (
          <div className="mt-[1rem] w-[40rem]">
            <CTACornerButton data={data.lastCta2} />
          </div>
        )}
      </div>
      <footer
        className="absolute bottom-[6rem] lg:bottom-[2.8rem] left-1/2 -translate-x-1/2 w-[min(226rem,90%)] px-[2rem] border-t border-white/20 pt-[3rem]
        flex flex-col items-center lg:flex-row gap-[1rem] justify-between text-white/70 text-[2rem] font-geistmono"
      >
        <span>©2025 Trademania. All rights reserved.</span>

        <div className="flex items-center gap-[2rem]">
          <Link href={"/impressum"} className="uppercase">
            Impressum
          </Link>
          <Link href={"/datenschutz"} className="uppercase">
            Datenschutz
          </Link>
        </div>

        <span>{data.lastFooterDate}</span>
      </footer>
    </section>
  );
}
